import { useFocusEffect, useNavigation } from 'expo-router';
import { useCallback, useLayoutEffect, useMemo } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';

import { useGetUserPurchasedGifts } from '@/api/hooks/giftShowHooks';
import { useGetAllShows } from '@/api/hooks/showHooks';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import ListEmptyComponent from '@/components/ListEmptyComponent';
import NextPageFetchingIndicator from '@/components/NextPageFetchingIndicator';
import Title from '@/components/Title';
import GiftHistoryItem from '@/components/trbShow/GiftHistoryItem';
import { i18n } from '@/utils/i18n';

const GiftsList = () => {
  const { height } = useWindowDimensions();
  const navigation = useNavigation();

  const initialN = useMemo(() => new Date().toISOString(), []);

  const { data: showData, isPending: allShowsPending } = useGetAllShows();
  const { data, isPending, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, refetch, isRefetching } =
    useGetUserPurchasedGifts({
      n: initialN
    });

  const gifts = data?.pages.flatMap((page) => page.results);

  //Locales
  const title = i18n.t('settings.screen_names.gifts_title');

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={title} />,
      headerLeft: () => <GoBackButton />
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  useFocusEffect(
    useCallback(() => {
      refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  return (
    <View style={[styles.mainContainer, Platform.OS === 'web' ? { height: height - 69 } : { flex: 1 }]}>
      {isPending || allShowsPending ? (
        <DotsLoader />
      ) : (
        <FlatList
          style={styles.fullWidth}
          showsVerticalScrollIndicator={false}
          data={gifts}
          scrollEnabled={true}
          keyExtractor={(item) => item.id}
          onEndReachedThreshold={0.7}
          onEndReached={(_) => {
            hasNextPage && !isFetching && fetchNextPage();
          }}
          onRefresh={refetch}
          refreshing={isRefetching}
          renderItem={({ item }) => (
            <GiftHistoryItem
              item={item}
              showData={{
                thumbnail: showData?.thumbnail ?? '[]',
                price: showData?.price ?? '-'
              }}
            />
          )}
          ItemSeparatorComponent={() => <Divider style={styles.divider} />}
          ListFooterComponent={isFetchingNextPage ? <NextPageFetchingIndicator /> : null}
          ListEmptyComponent={<ListEmptyComponent label={i18n.t('giftShows.gifts_empty_list')} />}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%'
  },

  fullWidth: {
    width: '100%'
  },

  divider: {
    marginVertical: 15
  },

  emptyList: {
    alignSelf: 'center',
    marginVertical: 25
  }
});

export default GiftsList;
